import { safeZodPath } from "@impero-com/common/utils/form/safeZodPath";
import { TFunction } from "@impero-com/common/utils/i18n";
import { z } from "zod";
import { ParsePayload } from "zod/v4/core";

import {
    CURRENCY_CODE_PATTERN,
    DATAGRID_MAX_NUMBER_OF_COLUMNS,
    DATAGRID_MAX_NUMBER_OF_ROWS,
    MAX_OPTION_LENGTH,
} from "components/fieldBuilder/consts";
import { Field } from "components/fieldBuilder/models";
import { findDependency } from "components/fieldBuilder/utils";
import { CONSTANTS } from "utils/constants";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const MAX_TITLE_LENGTH =
    CONSTANTS.FIELD_CONSTANTS.MAX_FIELD_TITLE_LENGTH;
export const MAX_INSTRUCTIONS_LENGTH = 10000;
export const MAX_BLOBS_PER_FIELD =
    CONSTANTS.COMMON_CONSTANTS.MAX_BLOBS_PER_FIELD;

// ---------------------------------------------------------------------------
// Option schemas (shared across checkbox/radio/dropdown)
// ---------------------------------------------------------------------------

/**
 * Note: We explicitly do not trim the option value.
 * Our existing data has options with leading and trailing spaces.
 * We do not want to change the existing data.
 *
 * Therefore we accept extra spaces and compare the options with condition values as they are.
 */
export const optionSchema = z.string().min(1).max(MAX_OPTION_LENGTH);

export function getOptionsSchema(t: TFunction) {
    return z.array(optionSchema).check((ctx) => {
        const options = ctx.value;
        options.forEach((option, index) => {
            if (options.indexOf(option) !== index) {
                ctx.issues.push({
                    message: t("FieldBuilder_Error_OptionAlreadyExists"),
                    path: [index],
                    code: "custom",
                    input: option,
                });
            }
        });
    });
}

// ---------------------------------------------------------------------------
// Common field type schemas
// ---------------------------------------------------------------------------

export const textInputFieldTypeSchema = z.object({
    type: z.literal("TextInput"),
});
export const textAreaFieldTypeSchema = z.object({
    type: z.literal("TextArea"),
});
export const numericInputFieldTypeSchema = z.object({
    type: z.literal("NumericInput"),
});
export const moneyInputFieldTypeSchema = z.object({
    type: z.literal("MoneyInput"),
    defaultCurrency: z.string().regex(CURRENCY_CODE_PATTERN),
});
export const fileUploadFieldTypeSchema = z.object({
    type: z.literal("FileUpload"),
});
export const headingFieldTypeSchema = z.object({
    type: z.literal("Heading"),
});

export function getRadioButtonGroupFieldTypeSchema(t: TFunction) {
    return z.object({
        type: z.literal("RadioButtonGroup"),
        options: getOptionsSchema(t),
    });
}

export function getCheckboxGroupFieldTypeSchema(t: TFunction) {
    return z.object({
        type: z.literal("CheckboxGroup"),
        options: getOptionsSchema(t),
    });
}

export function getDropdownFieldTypeSchema(t: TFunction) {
    return z.object({
        type: z.literal("Dropdown"),
        options: getOptionsSchema(t),
    });
}

// ---------------------------------------------------------------------------
// DataGrid field type schema
// ---------------------------------------------------------------------------

export const columnSchema = z.object({
    id: z.guid(),
    title: z.string().trim().min(1),
    type: z.literal(["Text", "Numeric"]),
});

function getUnrefinedColumnsSchema(t: TFunction) {
    return z
        .array(columnSchema)
        .min(1, t("FieldBuilder_Error_DataGrid_Columns"))
        .max(DATAGRID_MAX_NUMBER_OF_COLUMNS, {
            message: t("FieldBuilder_Error_DataGrid_Columns"),
        });
}

type UnrefinedColumns = z.infer<ReturnType<typeof getUnrefinedColumnsSchema>>;

function validateUniqueColumns(
    columns: UnrefinedColumns,
    ctx: ParsePayload,
    t: TFunction
) {
    const titleMap: Record<string, number[]> = {};
    columns.forEach((val, index) => {
        const trimmedTitle = val.title.trim().toLowerCase();
        if (titleMap[trimmedTitle]) {
            titleMap[trimmedTitle].push(index);
        } else {
            titleMap[trimmedTitle] = [index];
        }
    });
    const invalidIndices = Object.values(titleMap)
        .filter((indices) => indices.length > 1)
        .flatMap((indices) => indices);
    if (invalidIndices.length > 0) {
        invalidIndices.forEach((index) => {
            ctx.issues.push({
                message: t("FieldBuilder_Error_DataGrid_ColumnAlreadyExists"),
                code: "custom",
                path: safeZodPath<UnrefinedColumns>(`${index}`),
                input: ctx.value,
            });
        });
    }
}

export const getColumnsSchema = (t: TFunction) =>
    getUnrefinedColumnsSchema(t).check((ctx) =>
        validateUniqueColumns(ctx.value, ctx, t)
    );

function getInitialNumberOfRowsSchema(t: TFunction) {
    return z
        .int()
        .min(1, { error: t("FieldBuilder_Error_DataGrid_Rows") })
        .max(DATAGRID_MAX_NUMBER_OF_ROWS, {
            message: t("FieldBuilder_Error_DataGrid_Rows"),
        });
}

export function getDataGridFieldTypeSchema(t: TFunction) {
    return z.object({
        type: z.literal("DataGrid"),
        initialNumberOfRows: getInitialNumberOfRowsSchema(t),
        columns: getColumnsSchema(t),
    });
}
export type DataGridFieldType = z.infer<
    ReturnType<typeof getDataGridFieldTypeSchema>
>;

// ---------------------------------------------------------------------------
// Condition schema
// ---------------------------------------------------------------------------

export const conditionSchema = z.union([
    z.object({
        action: z.literal(["Show", "Hide"]),
        fieldId: z.guid(),
        operator: z.literal(["Equal", "NotEqual"]),
        value: z.string(),
    }),
    z.null(),
]);

// ---------------------------------------------------------------------------
// Base field schema (common properties across all fields)
// ---------------------------------------------------------------------------

export function getBaseFieldSchema(t: TFunction) {
    return z.object({
        id: z.guid(),
        title: z.string().trim().min(1).max(MAX_TITLE_LENGTH),
        instructions: z.string().trim().max(MAX_INSTRUCTIONS_LENGTH),
        mandatory: z.boolean(),
        condition: conditionSchema,
        blobIds: z.array(z.string()).max(
            MAX_BLOBS_PER_FIELD,
            t("FieldBuilder_Error_TooManyBlobs", {
                limit: MAX_BLOBS_PER_FIELD,
            })
        ),
    });
}

// ---------------------------------------------------------------------------
// Generic cross-field condition validation
// ---------------------------------------------------------------------------

/**
 * Validates field conditions (Show/Hide) across an array of fields.
 * Takes a `getConditionalValues` function to resolve valid condition values per field type,
 * making it reusable across control form and testing program form.
 */
export function validateFieldConditions<F extends Field<unknown, string>>(
    fields: F[],
    ctx: ParsePayload,
    t: TFunction,
    getConditionalValues: (fieldType: F["fieldType"]) => string[]
) {
    fields.forEach((field, index) => {
        const condition = field.condition;
        if (!condition) {
            return;
        }
        const dependency = findDependency(condition.fieldId, fields, field.id);
        switch (dependency.type) {
            case "FOUND": {
                const conditionalValues = getConditionalValues(
                    dependency.value.fieldType
                );
                if (!conditionalValues.includes(condition.value)) {
                    ctx.issues.push({
                        message: t(
                            "FieldBuilder_Error_Condition_Dependency_ValueNotFound"
                        ),
                        path: [index, "condition"],
                        code: "custom",
                        input: fields,
                    });
                }
                break;
            }
            case "FOUND_AFTER":
                ctx.issues.push({
                    message: t(
                        "FieldBuilder_Error_Condition_Dependency_FoundAfter"
                    ),
                    path: [index, "condition"],
                    code: "custom",
                    input: fields,
                });
                break;
            case "NOT_FOUND":
                ctx.issues.push({
                    message: t(
                        "FieldBuilder_Error_Condition_Dependency_NotFound"
                    ),
                    path: [index, "condition"],
                    code: "custom",
                    input: fields,
                });
                break;
        }
    });
}
